import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PageSEO } from "@/components/PageSEO";
import { ForfettariUpsellBox } from "@/components/ForfettariUpsellBox";
import { CheckupInviteBox } from "@/components/CheckupInviteBox";
import { sendAdsConversion, ADS_LABELS } from "@/lib/adsConversions";
import { SEO } from "@/lib/seo";
import {
  API, SERVICES, PROBLEMS, SOLUTIONS, METHOD_STEPS,
  TESTIMONIALS_DATA, LOCATIONS, SOCIAL_LINKS, WHATSAPP_NUMBER,
  CONTACT_EMAIL, TEAM_ITEMS
} from "@/lib/constants";
import {
  FileCheck, Compass, BarChart3, Phone, Mail, MapPin,
  Facebook, Instagram, Linkedin, Menu, X, Star, ArrowRight,
  CheckCircle, Users, Clock, Award, ChevronRight, MessageCircle,
  Building, Target, TrendingUp, Shield, Play, XCircle, Zap,
  Eye, Lock, AlertTriangle, Sparkles, ArrowDown, Edit, Trash2,
  Plus, Save, LogOut, Image, Download, ShoppingCart, FileSpreadsheet,
  HelpCircle, ChevronDown, CircleDollarSign, CalendarCheck, PieChart,
  FileText, Wallet, BadgePercent, Sparkle, RefreshCw, ShieldCheck, BookOpen
} from "lucide-react";

const DownloadPage = () => {
  const { token: tokenParam } = useParams();
  const location = useLocation();
  // Fallback: token in query (?t=...) — rescue per i link generati col bug della success_url
  const token = tokenParam || new URLSearchParams(location.search).get('t');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Link di download non valido');
      setLoading(false);
      return;
    }
    const check = async () => {
      try {
        const res = await axios.get(`${API}/products/download/${token}`);
        setData(res.data);
        // Meta Pixel: Purchase una volta sola per token, quando la conferma è arrivata.
        // Guard con localStorage sul token per evitare doppio evento su ricarica pagina.
        if (typeof window.fbq === 'function' && res.data.status !== 'pending') {
          try {
            const flagKey = `fbq_purchase_${token}`;
            if (!localStorage.getItem(flagKey)) {
              const purchaseParams = {
                content_name: res.data.product_title || res.data.title || '',
                content_ids: res.data.product_slug ? [res.data.product_slug] : undefined,
                content_type: 'product',
                value: Number(res.data.amount ?? res.data.product_price ?? res.data.price ?? 0),
                currency: (res.data.currency || 'EUR').toUpperCase(),
              };
              window.fbq('track', 'Purchase', purchaseParams, { eventID: `purchase_${token}` });
              // Google Ads / GA4 via GTM: pushiamo sul dataLayer un evento che il collega
              // puo' usare come trigger per Google Ads Conversion sugli strumenti.
              try {
                (window.dataLayer = window.dataLayer || []).push({
                  event: 'purchase_strumento',
                  ecommerce: {
                    transaction_id: token,
                    value: purchaseParams.value,
                    currency: purchaseParams.currency,
                    items: [{
                      item_id: res.data.product_slug || '',
                      item_name: purchaseParams.content_name,
                      price: purchaseParams.value,
                      quantity: 1,
                    }],
                  },
                });
              } catch (e) { /* noop */ }
              localStorage.setItem(flagKey, '1');
            }
          } catch (e) { /* noop */ }
        }
        if (res.data.status === 'pending') {
          // Retry after a few seconds
          setTimeout(check, 3000);
        } else {
          // Google Ads: conversione acquisto, una sola volta per acquisto.
          // Il controllo anti-doppione e la regola delle 24 ore sono lato backend.
          try {
            const flag = `ads_purchase_${token}`;
            if (!localStorage.getItem(flag)) {
              localStorage.setItem(flag, '1');
              const conv = await axios.post(`${API}/products/download/${token}/ads-conversion`);
              if (conv.data?.send) {
                sendAdsConversion(ADS_LABELS.acquistoProdotto, {
                  value: conv.data.value,
                  transaction_id: conv.data.transaction_id,
                });
              }
            }
          } catch (e) { /* noop */ }
        }
      } catch (e) {
        setError('Link di download non valido.');
      } finally {
        setLoading(false);
      }
    };
    check();
  }, [token]);

  if (loading) return (
    <main className="min-h-screen bg-[#F4EFEA] flex items-center justify-center">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#4A5568]">Verifica pagamento in corso...</p>
      </div>
    </main>
  );

  if (error) return (
    <main className="min-h-screen bg-[#F4EFEA] flex items-center justify-center p-6">
      <div className="text-center">
        <XCircle className="text-netflix mx-auto mb-4" size={48} />
        <h1 className="font-heading text-3xl text-[#0D1527] mb-4">ERRORE</h1>
        <p className="text-[#4A5568] mb-8">{error}</p>
        <Link to="/strumenti" className="text-gold hover:underline">Torna agli strumenti</Link>
      </div>
    </main>
  );

  if (data && data.status === 'pending') return (
    <main className="min-h-screen bg-[#F4EFEA] flex items-center justify-center p-6">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <h1 className="font-heading text-3xl text-[#0D1527] mb-4">PAGAMENTO IN VERIFICA</h1>
        <p className="text-[#4A5568]">Stiamo verificando il tuo pagamento. La pagina si aggiornera automaticamente...</p>
      </div>
    </main>
  );

  return (
    <main className="min-h-screen bg-[#F4EFEA] flex items-center justify-center p-6" data-testid="download-page">
      <div className="max-w-lg w-full text-center">
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="text-green-400" size={40} />
        </div>
        <h1 className="font-heading text-4xl text-[#0D1527] mb-4">ACQUISTO COMPLETATO!</h1>
        <p className="text-[#4A5568] mb-2">Grazie per il tuo acquisto di</p>
        <p className="font-heading text-2xl text-gold mb-8">{data.product_title}</p>

        <div className="space-y-4 mb-8">
          {data.has_file && (
            <a
              href={`${API}/products/download/${token}/file`}
              className="flex items-center justify-center gap-3 bg-gold text-black hover:bg-[#96742F] py-4 px-8 rounded-lg font-bold text-lg transition-all hover:scale-105 w-full"
              data-testid="download-file-btn"
            >
              <Download size={20} /> SCARICA IL FILE EXCEL
            </a>
          )}
          {data.has_instructions && (
            <a
              href={`${API}/products/download/${token}/instructions`}
              className="flex items-center justify-center gap-3 bg-[#EDE6DA] text-[#0D1527] hover:bg-white/20 py-4 px-8 rounded-lg font-bold transition-all w-full"
              data-testid="download-instructions-btn"
            >
              <Download size={20} /> SCARICA IL MANUALE (PDF)
            </a>
          )}
        </div>

        <div className="bg-white border border-[#E8E2D5] rounded-lg p-5 text-left mb-8">
          <p className="text-[#64708A] text-sm">
            Conserva questo link per scaricare di nuovo il file in futuro.
            {data.customer_email && <> Una copia e stata inviata a <span className="text-gold">{data.customer_email}</span>.</>}
          </p>
        </div>

        <ForfettariUpsellBox slug={data.product_slug} utmMedium="pagina_grazie" />
        <CheckupInviteBox slug={data.product_slug} utmMedium="pagina_grazie" />

        <Link to="/strumenti" className="text-gold hover:underline inline-flex items-center gap-2">
          <ArrowRight size={16} className="rotate-180" /> Torna agli strumenti
        </Link>
      </div>
    </main>
  );
};

// Payment Page (Public)

export { DownloadPage };
